import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit, Output,
} from '@angular/core';
import { combineLatest, forkJoin, Observable, of, Subject } from 'rxjs';
import { Select, Store } from '@ngxs/store';
import { SaveBorrowerValues } from 'app/store/borrower-value/borrower-value.actions';
import { BorrowerValueState } from 'app/store/borrower-value/borrower-value.state';
import { BorrowerValue } from 'app/models/dto/borrower-value.model';
import { DataListItem } from 'ng2-app/borrower-overview/data-list/data-list.component';
import { debounceTime, filter, map, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormlyDialogComponent, FormlyDialogData } from 'ng2-app/components/formly-dialog/formly-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { UntypedFormGroup } from '@angular/forms';
import { BorrowerValueService } from 'app/services/borrower-value/borrower-value.service';
import { FormlyModel } from 'app/form-core/formly/formly-core.config';
import _ from 'lodash';

import {Opportunity} from 'app/services/opportunity/opportunity.model';
import {OpportunityState} from 'app/store/opportunity/opportunity.state';
import {GetOpportunity} from 'app/store/opportunity/opportunity.actions';
import {Marketing} from 'app/services/marketing/marketing.model';
import {BorrowerValueSections} from 'app/services/borrower-value/borrower-value-sections';
import {Borrower} from 'app/models/dto/borrower.model';
import {DatePipe} from '@angular/common';
import {PhoneCountryCode} from 'app/models/dto/phone-country-code.model';
import {LocaleSettingService} from 'app/services/i18n/locale-setting.service';
import {Zone} from 'app/models/dto/country-data.model';
import {CountryDataState} from 'app/store/country/country-data.state';
import {LenderUserState} from 'app/store/lender-user/lender-user/lender-user.state';
import {LenderUser} from 'app/models/dto/lender-user.model';
import {ErcChecklistModel} from 'ng2-app/models/dto/erc/erc-checklist';
import {PostErcChecklist, PutErcChecklist} from 'ng2-app/store/erc/erc.actions';
import {ErcState} from 'ng2-app/store/erc/erc.state';

@Component({
  selector: 'app-borrower-values-list-smart',
  templateUrl: './borrower-values-list-smart.component.html',
  styleUrls: ['./borrower-values-list-smart.component.scss', '../../form-core/formly/formly-grid-styles.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BorrowerValuesListSmartComponent implements OnInit, OnDestroy {

  @Input() title: string;
  @Input() tab: string = 'overview';
  @Input() borrower: Borrower;
  @Input() newTableStyle: boolean;
  @Input() inContainer: boolean = true;
  @Input() basicStyle: boolean;
  @Input() fields: string[];
  @Output() businessStartDate;

  @Select(BorrowerValueState.loading) loadingBorrowerValues$: Observable<boolean>;
  @Select(BorrowerValueState.borrowerValues) allBorrowerValues$: Observable<BorrowerValue[]>;
  @Select(OpportunityState.opportunity) opportunity$: Observable<Opportunity>;
  @Select(CountryDataState.phoneCountryCodes) phoneCountryCodes$: Observable<PhoneCountryCode[]>;
  @Select(LenderUserState.lenderUser) lenderUser$: Observable<LenderUser>;
  @Select(ErcState.ercChecklist) ercChecklist$: Observable<ErcChecklistModel>;

  destroyed$ = new Subject<boolean>();
  borrowerValues$: Observable<BorrowerValue[]>;
  dataList$: Observable<DataListItem[]>;
  formlyDialogData$: Observable<FormlyDialogData>;
  borrowerValueSections;
  zones$: Observable<Zone[] | null>;
  zoneAliases = ['borrowerZone', 'ownerZone'];
  ercChecklist;

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private borrowerValueService: BorrowerValueService,
    private datePipe: DatePipe,
    private localeSettingService: LocaleSettingService,
  ) {
  }

  ngOnInit() {
    this.borrowerValueSections = new BorrowerValueSections(this.localeSettingService.getLocale()).borrowerValueSections(this.tab);

    if (this.title === 'Business Overview' || this.title === 'Business Details') {
      this.store.dispatch(new GetOpportunity(this.borrower.id));
    }

    this.borrowerValues$ = this.getBorrowerValues();
    this.borrowerValues$.pipe(
      takeUntil(this.destroyed$))
      .subscribe((bvs) => this.getOperationStartDateForBusiness(bvs));

    this.zones$ = this.store.select(CountryDataState.zonesByCountry).pipe(
      takeUntil(this.destroyed$),
      map(filterFn => filterFn(this.localeSettingService.getLocale().replace(/^[^A-Z]+/, ''))));
    this.dataList$ = this.buildDataList(this.borrowerValues$, this.opportunity$, this.phoneCountryCodes$, this.zones$);

    this.lenderUser$.pipe(
      take(1),
    ).subscribe(lenderUser => {
      this.formlyDialogData$ = this.borrowerValues$.pipe(
        map(borrowerValues => this.borrowerValuesToFormlyDialogData(borrowerValues, lenderUser, this.tab))
      );
    });

    this.ercChecklist$.pipe(
      takeUntil(this.destroyed$)
    )
      .subscribe((ercChecklist) => {
      this.ercChecklist = ercChecklist;
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  handleEdit() {
    this.formlyDialogData$.pipe(take(1)).subscribe(data => this.openDialog(data));
  }

  private getBorrowerValues(): Observable<BorrowerValue[]> {
    const orderBy: Array<string | Function> = ['group', 'groupOrder'];
    if (this.title === 'Business Overview' || this.title === 'Business Details') {
      orderBy.unshift(this.sortBusinessOverview);
    }
    if (this.title === 'Financing Preferences') {
      orderBy.unshift(this.sortFinancialPreferences);
    } else if (this.title === 'PPP Info') {
      orderBy.unshift(this.sortPPPInfoPreferences);
    } else if (this.title === 'Business Contact') {
      orderBy.unshift(this.sortBusinessContact);
    }
    return this.allBorrowerValues$.pipe(
      takeUntil(this.destroyed$),
      filter(borrowerValues => borrowerValues?.length > 0),
      map(borrowerValues => {
        const bv = _(borrowerValues)
          .filter(borrowerValue => borrowerValue.type !== 'document')
          .orderBy(orderBy)
          .value();
        return bv;
      })
    );
  }

  private sortFinancialPreferences(borrowerValue: BorrowerValue): number {
    if (borrowerValue.alias === 'ppp1SbaNumber') {
      return 0;
    } else if (borrowerValue.alias === 'currentBorrowerInterest') {
      return -1;
    } else {
      return 1;
    }
  }

  private sortPPPInfoPreferences(borrowerValue: BorrowerValue): number {
    if (borrowerValue.alias === 'pppLabel') {
      return 0;
    } else {
      return 1;
    }
  }

  private sortBusinessContact(borrowerValue: BorrowerValue): number {
    if (borrowerValue.alias === 'borrowerPhoneCountryCodeId') {
      return -3;
    } else if (borrowerValue.alias === 'borrowerPhone') {
      return -2;
    } else if (borrowerValue.alias === 'borrowerCanText') {
      return -1;
    } else if (borrowerValue.alias === 'marketingSmsOptIn') {
      borrowerValue.shortName = 'Opted In to Cross Selling Opportunities';
      return 0;
    } else {
      return 1;
    }
  }

  private sortBusinessOverview(borrowerValue: BorrowerValue): number {
    if (['doingBusinessAsName', 'averageMonthlyPayroll', 'number_of_employees'].includes(borrowerValue.alias)) {
      return 0;
    } else {
      return 1;
    }
  }

  private buildDataList(
    borrowerValues$: Observable<BorrowerValue[]>,
    opportunity$: Observable<Opportunity>,
    phoneCountryCodes$: Observable<PhoneCountryCode[]>,
    zones$: Observable<Zone[] | null>
  ): Observable<DataListItem[]> {
    const lists: Observable<DataListItem[]>[] = [];
    lists.push(combineLatest([phoneCountryCodes$, borrowerValues$, zones$]).pipe(
      switchMap(([phoneCountryCodes, borrowerValues, zones]) => {
        const items = borrowerValues
          .filter(borrowerValue => {
            const isLocaleUs: boolean = this.localeSettingService.getLocale() === 'en-US';
            const isBvZoneBv: boolean = this.zoneAliases.includes(borrowerValue.alias);
            const keepItemInList: boolean = isBvZoneBv ? isLocaleUs : true;
            return (
              (!this.fields || this.fields.includes(borrowerValue.alias)) &&
              this.borrowerValueSections[this.title].includes(borrowerValue.alias) &&
              borrowerValue.alias !== 'personal_credit_score_verified' && keepItemInList
            );
          })
          .map(borrowerValue =>
            this.borrowerValueService.borrowerValueToDataListItem(borrowerValues, borrowerValue, phoneCountryCodes, zones));
        return forkJoin([...items]);
      }),
      startWith([])
    ));

    if (this.title === 'Business Overview' || this.title === 'Business Details') {
      // merge in some opportunity marketing data
      lists.push(opportunity$.pipe(
        filter(opportunity => !!opportunity),
        map(opportunity => this.opportunityToDataList(opportunity))
      ));
      // merge in stuff from borrower object
      lists.push(of(
        this.borrowerToDataList(this.borrower)
          .filter(value => !this.fields || this.fields.includes(value.alias || ''))
      ));
    }

    return combineLatest(lists).pipe(
      map(x => x.flat())
    );
  }

  private borrowerValuesToFormlyDialogData(borrowerValues: BorrowerValue[], lenderUser: LenderUser, tab?: string): FormlyDialogData {
    const model = this.borrowerValueService.borrowerValuesToFormlyModel(borrowerValues);
    const fields = this.borrowerValueService.borrowerValuesToFormlyFieldConfigs(
      model,
      borrowerValues
        .filter(borrowerValue => this.borrowerValueSections[this.title].includes(borrowerValue.alias)),
      lenderUser,
      tab,
      this.businessStartDate,
      this.title
    );
    return this.formlyDialogData(model, fields);
  }

  private formlyDialogData(model: FormlyModel, fields: FormlyFieldConfig[]): FormlyDialogData {
    return {
      loading$: this.loadingBorrowerValues$,
      title: this.title,
      formlyConfig: {
        model,
        form: new UntypedFormGroup({}),
        fields,
        options: {}
      }
    };
  }

  private openDialog(data: FormlyDialogData) {
    const dialogRef = this.dialog.open(FormlyDialogComponent, {
      data,
      width: '25rem',
      maxHeight: '100vh',
    });

    dialogRef.componentInstance.saveData.pipe(
      takeUntil(this.destroyed$),
      switchMap(saveData => this.saveModel(saveData.formlyConfig.model, data)),
      tap(() => dialogRef.close())
    ).subscribe();
  }

  private saveModel(model: FormlyModel, data): Observable<any> {
    if (this.tab === 'erc') {
      let fieldsCount = 0;
      const aliases = this.borrowerValueSections[this.title];

      aliases.forEach((alias) => {
        if (model[alias] !== null) {
          fieldsCount++;
        }
      });

      if (this.title === 'Business Info' && fieldsCount >= 6) {
        this.ercChecklistUpdates(data);
      }

      if (fieldsCount === (aliases.length - this.countHiddenFormlyFields(data))) {
        this.ercChecklistUpdates(data);
      }
    }

    return this.borrowerValues$.pipe(
      take(1),
      map(borrowerValues => this.borrowerValueService.formlyModelToBorrowerValues(borrowerValues, model)),
      filter(borrowerValues => borrowerValues.length > 0),
      switchMap(borrowerValues => {
        return this.store.dispatch(new SaveBorrowerValues(this.borrower.id, borrowerValues));
      })
    );
  }

  private opportunityToDataList(opportunity: Opportunity): DataListItem[] {
    const marketing = opportunity.marketing || null;
    let dataList: DataListItem[] = [];
    if (
      marketing
      && ['Partner', 'Affiliate'].includes(marketing.medium)
    ) {
      dataList = dataList.concat(this.marketingToDataList(marketing));
    }
    return dataList;
  }

  private marketingToDataList(marketing: Marketing): DataListItem[] {
    return _(marketing)
      .pick([
        'campaign',
        'adgroup',
        'term',
      ])
      .toPairs()
      .map(pair => {
        const label = _.startCase(pair[0]);
        const value = pair[1] === null ? '' : String(pair[1]);
        return { label, value };
      })
      .value();
  }

  private borrowerToDataList(borrower: Borrower): DataListItem[] {
    return [
      {
        label: 'Date Added',
        value: this.datePipe.transform(borrower.createdTimestamp * 1000, 'mediumDate') || '',
        alias: 'dateAdded',
      },
    ];
  }

  private ercChecklistUpdates(data) {
    const page = this.setErcPage(data);
    if (this.ercChecklist) {
      this.store.dispatch(new PutErcChecklist(this.ercChecklist.id, this.borrower.id, page));
    } else {
      this.store.dispatch(new PostErcChecklist(this.borrower.id, page));
    }
  }

  private setErcPage(data) {
    let page = '';
    switch (data.title) {
      case 'Qualifications': return page = 'qualification';
      case 'Basic Info': return page = 'basicInfo';
      case 'Ownership Info': return page = 'ownershipInfo';
      case 'Business Info': return page = 'businessInfo';
      case 'Gross Receipts': return page = 'revenue';
      case 'Government Order Info': return page = 'governmentOrderInfo';
      case 'PEO Info': return page = 'peoInfo';
      case 'PPP Info': return page = 'pppInfo';
      case 'Grant & Credit Info': return page = 'grantCreditInfo';
    }
  }

  private getOperationStartDateForBusiness(borrowerValues: BorrowerValue[]) {
    borrowerValues.forEach(borrowerValue => {
      if (borrowerValue.alias === 'operation_start_date') {
        this.businessStartDate = borrowerValue.value;
        }
      }
    );
  }

  private countHiddenFormlyFields(data) {
    let result = 0;

    data.formlyConfig.fields.forEach((field) => {
      if(field.hideExpression === true || field.hideExpression()) {
        result++;
      }
    });
    return result;
  }
}
